import React, { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

const card = { background: '#1a1a2e', borderRadius: 12, padding: 16, marginBottom: 12 };
const badge = (color) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 6, background: color + '22', color, fontSize: 12, fontWeight: 'bold' });

export default function MyTaxes() {
  const { currentCompany } = useApp();
  const [data, setData] = useState(null);
  const { t } = useI18n();
  const [invoices, setInvoices] = useState([]);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const qs = currentCompany?.id ? `?company_id=${currentCompany.id}` : '';
    Promise.all([
      api.get(`/taxes/my${qs}`).catch(() => ({ data: null })),
      api.get(`/taxes/my/invoices${qs}`).catch(() => ({ data: [] })),
    ]).then(([taxRes, i]) => {
      setData(taxRes.data);
      setInvoices(Array.isArray(i.data) ? i.data : []);
      setLoading(false);
    });
  }, [currentCompany?.id]);

  if (loading) return <div style={{ padding: 20, textAlign: 'center' }}>{t('common.loading')}</div>;
  if (!data) return <div style={{ padding: 20, textAlign: 'center', color: '#888' }}>Нет данных о налогах</div>;

  const s = data.summary;
  const tabs = [
    { id: 'overview', label: '📊 Обзор' },
    { id: 'monthly', label: '📅 По месяцам' },
    { id: 'invoices', label: '🧾 Фактуры' },
    { id: 'optimize', label: '💡 Оптимизация' },
    { id: 'deadlines', label: '⏰ Сроки' },
  ];

  return (
    <div style={{ padding: 16, maxWidth: 800, margin: '0 auto' }}>
      <BackButton />
      <h1 style={{ fontSize: 24, marginBottom: 16 }}>🏦 Мои налоги {data.year}</h1>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, overflowX: 'auto' }}>
        {tabs.map(opt => (
          <button key={opt.id} onClick={() => setTab(opt.id)}
            style={{ padding: '8px 14px', background: tab === opt.id ? '#3b82f6' : '#222', color: '#fff',
              border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>
            {opt.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === 'overview' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
            <div style={{ ...card, background: 'linear-gradient(135deg, #1e3a5f, #1a1a2e)' }}>
              <div style={{ color: '#888', fontSize: 12 }}>💰 Доход (brutto)</div>
              <div style={{ fontSize: 28, fontWeight: 'bold' }}>{s.total_gross.toLocaleString()} zł</div>
            </div>
            <div style={{ ...card, background: 'linear-gradient(135deg, #3a1e1e, #1a1a2e)' }}>
              <div style={{ color: '#888', fontSize: 12 }}>📤 PIT уплачено</div>
              <div style={{ fontSize: 28, fontWeight: 'bold', color: '#ef4444' }}>{s.total_pit_paid.toLocaleString()} zł</div>
            </div>
            <div style={{ ...card }}>
              <div style={{ color: '#888', fontSize: 12 }}>✅ На руки (netto)</div>
              <div style={{ fontSize: 22, fontWeight: 'bold', color: '#10b981' }}>{s.total_net.toLocaleString()} zł</div>
            </div>
            <div style={{ ...card }}>
              <div style={{ color: '#888', fontSize: 12 }}>📊 Эффективная ставка</div>
              <div style={{ fontSize: 22, fontWeight: 'bold' }}>{s.effective_tax_rate}%</div>
            </div>
          </div>

          {/* VAT summary */}
          <div style={card}>
            <h3 style={{ margin: '0 0 8px' }}>🧾 VAT</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#888' }}>VAT на фактурах (PLN):</span>
              <strong>{data.vat.total_vat_on_invoices.toLocaleString()} zł</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#888' }}>🇵🇱 Фактуры PLN (brutto):</span>
              <strong>{(data.vat.total_invoices_gross_pln || data.vat.total_invoices_gross || 0).toLocaleString()} zł</strong>
            </div>
            {(data.vat.total_invoices_gross_eur || 0) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#888' }}>🇪🇺 Фактуры EUR (0% VAT):</span>
                <strong>{data.vat.total_invoices_gross_eur.toLocaleString()} €</strong>
              </div>
            )}
            {data.vat.note_eur && (data.vat.total_invoices_gross_eur || 0) > 0 && (
              <div style={{ fontSize: 11, color: '#888', marginTop: 6, fontStyle: 'italic' }}>
                ℹ️ {data.vat.note_eur}
              </div>
            )}
          </div>

          {/* Forecast */}
          <div style={card}>
            <h3 style={{ margin: '0 0 8px' }}>🔮 Прогноз на {data.year}</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#888' }}>Годовой доход:</span>
              <strong>{data.forecast.annual_gross.toLocaleString()} zł</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#888' }}>Годовой PIT:</span>
              <strong style={{ color: '#ef4444' }}>{data.forecast.annual_pit.toLocaleString()} zł</strong>
            </div>
            {data.forecast.second_bracket && (
              <div style={{ marginTop: 8, padding: 8, background: '#ef444422', borderRadius: 8, color: '#ef4444' }}>
                ⚠️ Прогноз превышает 120 000 PLN — ставка PIT вырастет до 32%!
              </div>
            )}
          </div>

          {/* KUP info */}
          <div style={card}>
            <h3 style={{ margin: '0 0 8px' }}>📋 Koszty uzyskania przychodu (KUP)</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#888' }}>KUP всего:</span>
              <strong>{s.total_kup.toLocaleString()} zł</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#888' }}>ZUS уплачено:</span>
              <strong>{s.total_zus_paid.toLocaleString()} zł</strong>
            </div>
          </div>
        </>
      )}

      {/* Monthly */}
      {tab === 'monthly' && (
        <div>
          {Object.entries(data.monthly).map(([m, v]) => (
            <div key={m} style={card}>
              <h3 style={{ margin: '0 0 8px' }}>📅 {m}</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, fontSize: 14 }}>
                <div><span style={{ color: '#888' }}>Brutto:</span><br /><strong>{v.gross.toLocaleString()} zł</strong></div>
                <div><span style={{ color: '#888' }}>PIT:</span><br /><strong style={{ color: '#ef4444' }}>{v.pit.toLocaleString()} zł</strong></div>
                <div><span style={{ color: '#888' }}>Netto:</span><br /><strong style={{ color: '#10b981' }}>{v.net.toLocaleString()} zł</strong></div>
              </div>
              {v.kup > 0 && <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>KUP: {v.kup.toLocaleString()} zł</div>}
            </div>
          ))}
          {Object.keys(data.monthly).length === 0 && <p style={{ color: '#888', textAlign: 'center' }}>Нет данных</p>}
        </div>
      )}

      {/* Invoices */}
      {tab === 'invoices' && (
        <div>
          {invoices.length === 0 ? (
            <p style={{ color: '#888', textAlign: 'center' }}>Нет фактур</p>
          ) : invoices.map(inv => (
            <div key={inv.id} style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong>{inv.invoice_number || `#${inv.id}`}</strong>
                  <span style={{ marginLeft: 8, ...badge(inv.status === 'paid' ? '#10b981' : '#f59e0b') }}>
                    {inv.status}
                  </span>
                </div>
                <span style={{ color: '#888', fontSize: 12 }}>{inv.issue_date}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 8, fontSize: 14 }}>
                <div><span style={{ color: '#888' }}>Netto:</span><br />{inv.net_total.toLocaleString()} {inv.currency === 'EUR' ? '€' : 'zł'}</div>
                <div><span style={{ color: inv.vat_rate > 0 ? '#ef4444' : '#10b981' }}>VAT {inv.vat_rate}%{inv.currency === 'EUR' && inv.vat_rate === 0 ? ' (EU)' : ''}:</span><br />{inv.vat_total.toLocaleString()} {inv.currency === 'EUR' ? '€' : 'zł'}</div>
                <div><span style={{ color: '#888' }}>Brutto:</span><br /><strong>{inv.gross_total.toLocaleString()} {inv.currency === 'EUR' ? '€' : 'zł'}</strong></div>
              </div>
              {inv.currency === 'EUR' && inv.vat_rate === 0 && (
                <div style={{ fontSize: 11, color: '#10b981', marginTop: 4 }}>🇪🇺 Intra-EU (WDT) — reverse charge</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Optimization */}
      {tab === 'optimize' && (
        <div>
          {(data.optimizations || []).map(tip => (
            <div key={tip.id} style={{ ...card, borderLeft: `3px solid ${tip.risk === 'high' ? '#ef4444' : tip.risk === 'medium' ? '#f59e0b' : '#10b981'}` }}>
              <h3 style={{ margin: '0 0 6px' }}>{tip.title}</h3>
              <p style={{ color: '#ccc', margin: '0 0 6px', fontSize: 14 }}>{tip.description}</p>
              {tip.potential_saving && (
                <div style={badge('#10b981')}>💰 Экономия до: {tip.potential_saving.toLocaleString()} PLN</div>
              )}
              {tip.potential_extra_tax && (
                <div style={badge('#ef4444')}>⚠️ Доп. налог: +{tip.potential_extra_tax.toLocaleString()} PLN</div>
              )}
              {tip.action && <p style={{ color: '#3b82f6', fontSize: 13, marginTop: 4 }}>📌 {tip.action}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Deadlines */}
      {tab === 'deadlines' && (
        <div>
          {(data.next_deadlines || []).map((d, i) => {
            const daysLeft = Math.ceil((new Date(d.date) - new Date()) / 86400000);
            const urgent = daysLeft <= 5;
            return (
              <div key={i} style={{ ...card, borderLeft: `3px solid ${urgent ? '#ef4444' : '#3b82f6'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <strong>{d.type}</strong>
                    <p style={{ color: '#888', margin: '2px 0', fontSize: 13 }}>{d.description}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 16, fontWeight: 'bold' }}>{d.date}</div>
                    <div style={{ ...badge(urgent ? '#ef4444' : '#3b82f6') }}>
                      {daysLeft <= 0 ? '❗ Просрочено' : `${daysLeft} дн.`}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
